import React, { useState, useMemo } from "react";
import {
  Modal,
  Button,
  Table,
  TextInput,
  Stack,
  Text,
  Select,
  ScrollArea,
  Pagination,
  Flex,
  Avatar,
  useMantineTheme,
  Loader,
  Alert,
} from "@mantine/core";
import { IconSearch, IconUser, IconAlertCircle } from "@tabler/icons-react";
import css from "./ImpersonateUserModal.module.scss";
import { getOrgUsers } from "api";
import { useQuery } from "react-query";
import { useUser } from "stores/useUser";
import { ONEDAY } from "utils/common";

const ImpersonateUserModal = ({ opened, setOpened, onImpersonate, isImpersonating }) => {
  const theme = useMantineTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [impersonatingUserId, setImpersonatingUserId] = useState(null);
  const { user } = useUser();

  const { data: users, isLoading, isError } = useQuery({
    queryKey: ["orgUsers", user?.subscription?.organization_id],
    queryFn: () => getOrgUsers(user.subscription.organization_id),
    enabled: !!user?.subscription?.organization_id && opened,
    staleTime: ONEDAY,
  });

  const handleClose = () => {
    setOpened(false);
    setSearchQuery("");
    setCurrentPage(1);
    setImpersonatingUserId(null);
  };

  const handleImpersonate = (user) => {
    console.log("Impersonating user:", user);
    setImpersonatingUserId(user.uid);
    if (onImpersonate) {
      onImpersonate(user);
    }
    // Don't close the modal immediately - let the success/error handler do it
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    if (!users || !Array.isArray(users)) return [];

    // First, filter out any invalid users that might have slipped through
    let validUsers = users.filter((user) => {
      return (
        user.email &&
        user.email !== "null" &&
        user.email !== "N/A" &&
        user.displayName &&
        user.displayName !== "null" &&
        user.displayName !== "N/A" &&
        user.uid &&
        user.uid !== "null"
      );
    });

    // Then apply search filter
    let filtered = validUsers.filter((user) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        (user.displayName?.toLowerCase() || "").includes(searchLower) ||
        (user.email?.toLowerCase() || "").includes(searchLower) ||
        (user.department?.toLowerCase() || "").includes(searchLower)
      );
    });

    // Sort with proper null handling
    if (sortBy) {
      filtered.sort((a, b) => {
        let aVal, bVal;

        // Map sortBy to correct field names
        if (sortBy === "name") {
          aVal = a.displayName || "";
          bVal = b.displayName || "";
        } else {
          aVal = a[sortBy] || "";
          bVal = b[sortBy] || "";
        }

        if (typeof aVal === "string") {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (sortOrder === "asc") {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    return filtered;
  }, [users, searchQuery, sortBy, sortOrder]);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedUsers.slice(startIndex, endIndex);
  }, [filteredAndSortedUsers, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAndSortedUsers.length / pageSize);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex justify="center" align="center" py="xl">
          <Loader />
          <Text ml="md">Loading users...</Text>
        </Flex>
      );
    }

    if (isError) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Users"
          color="red"
          variant="light"
        >
          Failed to load users. Please try again later.
        </Alert>
      );
    }

    return (
      <>
        {/* Search Bar */}
        <TextInput
          placeholder="Search users by name, email, or department..."
          icon={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.currentTarget.value);
            setCurrentPage(1);
          }}
          style={{ minWidth: "100%" }}
        />

        {/* Table Container with Fixed Height */}
        <div style={{ height: "400px" }}>
          <ScrollArea h={400} type="auto">
            <Table striped highlightOnHover>
              <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1 }}>
                <tr>
                  <th
                    style={{ cursor: "pointer", minWidth: "180px" }}
                    onClick={() => handleSort("name")}
                  >
                    Name {sortBy === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    style={{ cursor: "pointer", minWidth: "220px" }}
                    onClick={() => handleSort("email")}
                  >
                    Email {sortBy === "email" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    style={{ cursor: "pointer", minWidth: "150px" }}
                    onClick={() => handleSort("department")}
                  >
                    Department{" "}
                    {sortBy === "department" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th style={{ minWidth: "150px" }}>Impersonate</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <Text ta="center" c="dimmed" py="xl">
                        {searchQuery
                          ? "No users match your search criteria."
                          : "No users available."}
                      </Text>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((userItem) => (
                    <tr key={userItem.uid}>
                      <td>
                        <Flex align="center" gap="sm">
                          <Avatar
                            src={userItem.photoURL}
                            name={userItem.displayName}
                            color="initials"
                            size="sm"
                          />
                          <Text size="sm">{userItem.displayName || "N/A"}</Text>
                        </Flex>
                      </td>
                      <td>
                        <Text size="sm" title={userItem.email}>
                          {userItem.email || "N/A"}
                        </Text>
                      </td>
                      <td>
                        <Text size="sm">{userItem.department || "N/A"}</Text>
                      </td>
                      <td>
                        <Button
                          size="xs"
                          leftSection={<IconUser size={16} />}
                          onClick={() => handleImpersonate(userItem)}
                          variant="light"
                          color="blue"
                          loading={isImpersonating && impersonatingUserId === userItem.uid}
                          disabled={isImpersonating && impersonatingUserId !== userItem.uid}
                        >
                          Impersonate
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </ScrollArea>
        </div>

        {!isLoading && !isError && (
          <>
            {/* Pagination Controls */}
            <Flex align="center" justify="space-between">
              <Select
                label="Page size"
                value={pageSize.toString()}
                onChange={(value) => {
                  setPageSize(parseInt(value));
                  setCurrentPage(1);
                }}
                data={[
                  { value: "5", label: "5 per page" },
                  { value: "10", label: "10 per page" },
                  { value: "25", label: "25 per page" },
                ]}
                style={{ minWidth: 120 }}
              />

              {totalPages > 1 && (
                <Pagination
                  value={currentPage}
                  onChange={setCurrentPage}
                  total={totalPages}
                  size="sm"
                />
              )}
            </Flex>

            <Text size="sm" c="dimmed" ta="center">
              Showing {paginatedUsers.length} of {filteredAndSortedUsers.length}{" "}
              users
            </Text>
          </>
        )}
      </>
    );
  };

  return (
    <Modal
      overlayColor={
        theme.colorScheme === "dark"
          ? theme.colors.dark[9]
          : theme.colors.gray[2]
      }
      overlayOpacity={0.55}
      overlayBlur={3}
      opened={opened}
      onClose={handleClose}
      size="1000px"
      title="Impersonate User"
      className={css.modal}
      centered
    >
      <Stack gap="md" className={css.container}>
        {renderContent()}
      </Stack>
    </Modal>
  );
};

export default ImpersonateUserModal;
